import type { OBB, Vec2 } from '../../geo/types';
import { obbCorners } from '../../geo/polygon';
import { obbOverlap } from '../../geo/sat';
import type { Lot } from './types';
import type { Plot } from './plots';
import { across } from './acrossPlot';
import { lotKey } from './lotKey';
import { LOT } from './lotRules';
import { obbToAABB, obbsMayMeet } from './lotObb';
import { createCellGrid } from './cellGrid';

/**
 * PLAN Lots: a building across its plot widens sideways onto the ground no plot reaches beside it,
 * the slivers between plots and the wedges where plots fan out on a bend, so that ground goes to the
 * plots beside it: the woodcut of 1544 draws no strip or wedge between the buildings behind the
 * fronts, and the painting paints that ground built. It widens by up to `reach` metres a side, found
 * to within `reach` / 2^`bisect`, never onto another house's plot, built on or not, nor into a yard
 * the sources show open, and only as far as the lots and reservations standing let it; a side that
 * would gain less than `least` stays as it is.
 */
export const WIDEN = { reach: 3, bisect: 6, least: 0.1 } as const;

/** The ground the plots take, each its house's. */
export interface PlotGround {
  /** Whether a box reaches onto the plot of any house but `own`. */
  onOtherPlot(obb: OBB, own: Lot): boolean;
}

interface Filed {
  readonly key: string;
  readonly obb: OBB;
}

export function createPlotGround(plots: readonly Plot[]): PlotGround {
  const grid = createCellGrid<Filed>(16);
  for (const plot of plots) {
    if (plot.depth <= 0) continue;
    const obb = across(plot.lot, 0, plot.depth, 1);
    grid.insert({ key: lotKey(plot.lot), obb }, obbToAABB(obb));
  }
  return {
    onOtherPlot: (obb, own) => {
      const key = lotKey(own);
      return grid.some(
        obbToAABB(obb),
        (other) =>
          other.key !== key &&
          obbsMayMeet(obb, other.obb) &&
          obbOverlap(obb, other.obb, LOT.overlapTolerance)
      );
    },
  };
}

/** What a widened building must keep clear of. */
export interface WidenSite {
  /** Whether a building fits among the lots and reservations standing. */
  readonly fits: (obb: OBB) => boolean;
  readonly ground: PlotGround;
  readonly inYard: (p: Vec2) => boolean;
}

/* The box moved out by `by` on one side along its axis: `side` -1 toward -axisU, +1 toward +axisU. */
function grown(obb: OBB, side: -1 | 1, by: number): OBB {
  const [ax, ay] = obb.axisU;
  return {
    ...obb,
    center: [obb.center[0] + (ax * side * by) / 2, obb.center[1] + (ay * side * by) / 2],
    halfU: obb.halfU + by / 2,
  };
}

/* The strip a side grows by. */
function strip(obb: OBB, side: -1 | 1, by: number): OBB {
  const [ax, ay] = obb.axisU;
  const out = obb.halfU + by / 2;
  return {
    ...obb,
    center: [obb.center[0] + ax * side * out, obb.center[1] + ay * side * out],
    halfU: by / 2,
  };
}

function grows(obb: OBB, house: Lot, side: -1 | 1, by: number, site: WidenSite): boolean {
  const added = strip(obb, side, by);
  return (
    ![added.center, ...obbCorners(added)].some(site.inYard) &&
    !site.ground.onOtherPlot(added, house) &&
    site.fits(grown(obb, side, by))
  );
}

/*
 * The furthest a side may grow: all of the reach, else the last of the halvings that fits, less the
 * overlap the lots allow each other, so it stands wall to wall with its neighbour, not into it.
 */
function reachOf(obb: OBB, house: Lot, side: -1 | 1, site: WidenSite): number {
  if (grows(obb, house, side, WIDEN.reach, site)) return WIDEN.reach;
  let lo = 0;
  let hi: number = WIDEN.reach;
  for (let i = 0; i < WIDEN.bisect; i++) {
    const mid = (lo + hi) / 2;
    if (grows(obb, house, side, mid, site)) lo = mid;
    else hi = mid;
  }
  return lo - LOT.overlapTolerance;
}

/** A building across `house`'s plot, widened on each side onto the free ground beside it. */
export function widen(obb: OBB, house: Lot, site: WidenSite): OBB {
  let out = obb;
  for (const side of [-1, 1] as const) {
    const by = reachOf(out, house, side, site);
    if (by >= WIDEN.least) out = grown(out, side, by);
  }
  return out;
}
